// Edge Mentor development diagnostics.
//
// These messages are console-only. They do not change UI behavior, routing,
// Excel/JSON content, timers, video previews, or form state.
//
// They are intentionally disabled in production builds.

const DIAGNOSTICS_ENABLED =
  (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "development"
  ) ||
  (
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(
      window.location.hostname,
    )
  );

const emittedDiagnostics = new Set();

function printOnce(key, printer) {
  if (!DIAGNOSTICS_ENABLED) {
    return;
  }

  if (emittedDiagnostics.has(key)) {
    return;
  }

  emittedDiagnostics.add(key);
  printer();
}

export function edgeMentorDiagnostic(type, payload = {}) {
  if (!DIAGNOSTICS_ENABLED) {
    return;
  }

  if (type === "unresolved-control") {
    const uiField = String(payload.a ?? "").trim();

    if (!uiField) {
      return;
    }

    const key = `unresolved-control|${uiField}`;

    printOnce(key, () => {
      console.warn(
        `[Edge Mentor] Unresolved control\n` +
          `UI field label: ${uiField}\n` +
          `No code_label could be resolved.`,
      );
    });

    return;
  }

  if (type === "missing-content") {
    const uiField = String(payload.a ?? "").trim();
    const codeLabel = String(payload.b ?? "").trim();
    const entryCount = Number(payload.n) || 0;

    const key =
      `missing-content|${uiField}|${codeLabel}|${entryCount}`;

    printOnce(key, () => {
      console.warn(
        `[Edge Mentor] Missing content\n` +
          `UI field label: ${uiField || "(unknown)"}\n` +
          `Resolved code_label: ${codeLabel || "(empty)"}\n` +
          `No matching JSON entry found.\n` +
          `Runtime entries: ${entryCount}`,
      );
    });

    return;
  }

  if (type === "video-load-failed") {
    const uiLabel = String(payload.uiLabel ?? "").trim();
    const codeLabel = String(payload.codeLabel ?? "").trim();
    const videoSource = String(payload.videoSource ?? "").trim();
    const sourceType = String(payload.sourceType ?? "").trim();
    const mediaError = String(payload.mediaError ?? "").trim();

    const key =
      `video-load-failed|${codeLabel}|${videoSource}|${mediaError}`;

    printOnce(key, () => {
      console.warn(
        `[Edge Mentor] Video load failed\n` +
          `UI label: ${uiLabel || "(unknown)"}\n` +
          `code_label: ${codeLabel || "(empty)"}\n` +
          `Source type: ${sourceType || "(unknown)"}\n` +
          `Video source: ${videoSource || "(empty)"}\n` +
          `Media error: ${mediaError || "(unknown)"}`,
      );
    });

    return;
  }

  if (type === "video-source-unsupported") {
    const uiLabel = String(payload.uiLabel ?? "").trim();
    const codeLabel = String(payload.codeLabel ?? "").trim();
    const videoSource = String(payload.videoSource ?? "").trim();
    const sourceType = String(payload.sourceType ?? "").trim();

    const key =
      `video-source-unsupported|${codeLabel}|${videoSource}`;

    printOnce(key, () => {
      console.warn(
        `[Edge Mentor] Unsupported video source\n` +
          `UI label: ${uiLabel || "(unknown)"}\n` +
          `code_label: ${codeLabel || "(empty)"}\n` +
          `Source type: ${sourceType || "(unknown)"}\n` +
          `Video source: ${videoSource || "(empty)"}`,
      );
    });

    return;
  }

  if (type === "duplicate-runtime-code") {
    const codeLabel = String(payload.codeLabel ?? "").trim();
    const duplicateCount = Number(payload.count) || 0;

    const key =
      `duplicate-runtime-code|${codeLabel}|${duplicateCount}`;

    printOnce(key, () => {
      console.warn(
        `[Edge Mentor] Duplicate runtime content\n` +
          `code_label: ${codeLabel || "(empty)"}\n` +
          `Duplicate count: ${duplicateCount}`,
      );
    });
  }
}

export function edgeMentorScanContent(entries) {
  if (!DIAGNOSTICS_ENABLED || !Array.isArray(entries)) {
    return;
  }

  const seen = new Set();
  const duplicateCounts = new Map();

  entries.forEach((item) => {
    const codeLabel = String(
      item?.code_label ?? "",
    ).trim();

    if (!codeLabel) {
      return;
    }

    const normalized = codeLabel.toLowerCase();

    if (seen.has(normalized)) {
      duplicateCounts.set(
        codeLabel,
        (duplicateCounts.get(codeLabel) || 0) + 1,
      );
      return;
    }

    seen.add(normalized);
  });

  duplicateCounts.forEach((extraCount, codeLabel) => {
    edgeMentorDiagnostic("duplicate-runtime-code", {
      codeLabel,
      count: extraCount + 1,
    });
  });
}
